import { CrewErrorBoundary } from 'components/functions/crewErrorBoundary'
import { memo, FC, useMemo, useEffect, useCallback } from 'react'
import { GroupChatPanel } from './components/groupChatPanel/groupChatPanel'
import { EntityType } from '@crew/enums/domain'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { useChatCurrentService } from '@crew/states'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetChatRoomByEntityQuery } from '@crew/apis/chat/chatApis'
import { useGetProjectQuery } from '@crew/apis/project/projectApis'
import { GetProjectRequest } from '@crew/apis/project/models/getProject/request'
import { GetChatRoomByEntityRequest } from '@crew/apis/chat/models/getChatRoomByEntity/request'
import { useValueChangeEffect } from '@crew/hooks'
import { useUnmount } from '@dx-system/react-use'
import { useCrewNavigate } from 'hooks/useCrewNavigate'

export const GroupChatPage: FC = memo(() => {
  const { projectId } = useParams()

  const dispatch = useAppDispatch()
  const { navigate } = useCrewNavigate()

  const groupChatEventMessage = useAppSelector(
    (state) => state.app.projectEventMessage
  )
  const favoriteEventMessage = useAppSelector(
    (state) => state.app.favoriteEventMessage
  )

  // Sliceの操作を行うためのServiceを取得
  const chatCurrentService = useChatCurrentService(dispatch)

  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

  // グループチャットプロジェクトを取得する
  // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用している
  const getProjectForGroupChatParam: GetProjectRequest | undefined = projectId
    ? {
        projectId,
      }
    : undefined
  const {
    data: getGroupChatResult,
    refetch: refetchGroupChat,
    isLoading: isLoadingGroupChat,
    isError: isErrorGroupChat,
  } = useGetProjectQuery(getProjectForGroupChatParam ?? skipToken)

  // プロジェクトに参加済みかどうか
  const isJoinedProject = useMemo(
    () =>
      loggedInUser && getGroupChatResult?.project
        ? // プロジェクトのメンバーなら参加済として扱う
          getGroupChatResult.project.projectMembers.some(
            (projectMember) => loggedInUser.id === projectMember.id
          ) || // プロジェクトのオーナーなら参加済として扱う
          loggedInUser.id === getGroupChatResult.project.ownerUser.id
        : false, // 判定するための情報が足りない場合は未参加として扱う
    [getGroupChatResult?.project, loggedInUser]
  )

  // グループチャット再読み込みのトリガー
  useEffect(() => {
    if (projectId) {
      refetchGroupChat()
    }
  }, [projectId, groupChatEventMessage, refetchGroupChat, favoriteEventMessage])

  // チャットルーム取得にはグループチャットが存在している必要があるので、取得できない場合はチャットルーム取得処理を実行しない
  const getChatRoomParams: GetChatRoomByEntityRequest | undefined =
    getGroupChatResult?.project?.id
      ? {
          entityType: EntityType.Project,
          entityRecordId: getGroupChatResult?.project?.id,
        }
      : undefined

  // グループチャットが存在する場合、チャットルームを取得する
  const { data: getChatRoomResponse } = useGetChatRoomByEntityQuery(
    getChatRoomParams ?? skipToken
  )

  // 取得したチャットルームIDに紐づくチャットメッセージを取得する
  useValueChangeEffect(
    () => {
      if (!isJoinedProject) {
        // 未参加の場合はチャットルームを表示しない
        chatCurrentService.resetCurrentChatRoom()
      } else {
        if (getChatRoomResponse?.chatRoom && getGroupChatResult?.project?.id) {
          // 表示するチャットルームを設定する
          chatCurrentService.setCurrentChatRoomAndRestoreChatThread({
            id: getChatRoomResponse.chatRoom.id,
            rootEntityType: EntityType.Project,
            rootEntityRecordId: getGroupChatResult.project.id,
            entityType: EntityType.Project,
            entityRecordId: getGroupChatResult.project.id,
          })
        }
      }
    },
    // チャットルーム変更時とグループチャットが追加されたときのみ処理する
    [
      chatCurrentService,
      getChatRoomResponse?.chatRoom,
      getChatRoomResponse?.chatRoom?.id,
      getGroupChatResult?.project?.id,
      isJoinedProject,
    ],
    [getChatRoomResponse?.chatRoom?.id, isJoinedProject]
  )

  const handleGroupChatDeleted = useCallback(() => {
    navigate('/projects')
  }, [navigate])

  // アンマウント時の処理
  useUnmount(() => {
    // 現在のチャットルーム情報をリセット
    chatCurrentService.resetCurrentChatRoom()
  })

  // プロジェクトが存在しない場合はコンテンツを表示しない
  if (!getGroupChatResult?.project) {
    return null
  }

  return (
    <CrewErrorBoundary>
      {/* グループチャットパネル */}
      <GroupChatPanel
        groupChat={getGroupChatResult.project}
        isLoadingGroupChat={isLoadingGroupChat}
        isErrorGroupChat={isErrorGroupChat}
        isJoinedGroupChat={isJoinedProject}
        onDeletedGroupChat={handleGroupChatDeleted}
      />
    </CrewErrorBoundary>
  )
})
